import { ValidatorRules } from '../../../tradebuilder-v2/util/validator'
import { EditStrategiesState } from '../edit-strategy-slice'

type Values = {
  default: number
  max: number
  min: number
}

function isTripleValid(strategy: string, requiredStrategies: string[], values: Values) {
  const isValid =
    !requiredStrategies.includes(strategy) ||
    (values.min >= 0 && values.default >= values.min && values.max >= values.default)

  return isValid
}

function validateMaxValue(
  strategy: string,
  requiredStrategies: string[],
  value: number,
  max: number
) {
  return !requiredStrategies.includes(strategy) || value <= max
}

function validateMinValue(
  strategy: string,
  requiredStrategies: string[],
  value: number,
  min: number
) {
  return !requiredStrategies.includes(strategy) || value >= min
}

export const editStrategyRules: ValidatorRules<EditStrategiesState> = {
  prot_levels: [
    (state: EditStrategiesState) => ({
      isValid: isTripleValid(
        state.selectedStrategy.strategyTemplateId,
        ['TermProtection', 'ContinuousProtection'],
        {
          min: state.selectedStrategy.minProtectionLevel,
          default: state.selectedStrategy.defaultProtectionLevel,
          max: state.selectedStrategy.maxProtectionLevel
        }
      ),
      message:
        'Valid protection levels must be entered. Max must be greater than default, and default must be greater than min'
    })
  ],
  max_cts_prot_level: [
    (state: EditStrategiesState) => ({
      isValid: validateMaxValue(
        state.selectedStrategy.strategyTemplateId,
        ['ContinuousProtection'],
        state.selectedStrategy.maxProtectionLevel,
        0.95
      ),
      message: 'Max protection level cannot be greater than 95%'
    })
  ],
  max_term_prot_level: [
    (state: EditStrategiesState) => ({
      isValid: validateMaxValue(
        state.selectedStrategy.strategyTemplateId,
        ['TermProtection'],
        state.selectedStrategy.maxProtectionLevel,
        1
      ),
      message: 'Max protection level cannot be greater than 100%'
    })
  ],
  leverage_ratios: [
    (state: EditStrategiesState) => ({
      isValid: isTripleValid(
        state.selectedStrategy.strategyTemplateId,
        [
          'ConstantProportionLeverage',
          'VolatilityControlLeverage',
          'TermProtection',
          'ContinuousProtection'
        ],
        {
          min: state.selectedStrategy.minLeverageRatio,
          default: state.selectedStrategy.defaultLeverageRatio,
          max: state.selectedStrategy.maxLeverageRatio
        }
      ),
      message:
        'Valid leverage ratios must be entered. Max must be greater than default, and default must be greater than min'
    })
  ],
  min_lev_ratio: [
    (state: EditStrategiesState) => ({
      isValid: validateMinValue(
        state.selectedStrategy.strategyTemplateId,
        [
          'ConstantProportionLeverage',
          'VolatilityControlLeverage',
          'TermProtection',
          'ContinuousProtection'
        ],
        state.selectedStrategy.minLeverageRatio,
        1
      ),
      message: 'Min leverage ratio cannot be less than 1'
    })
  ],
  max_lev_ratio: [
    (state: EditStrategiesState) => ({
      isValid: validateMaxValue(
        state.selectedStrategy.strategyTemplateId,
        [
          'ConstantProportionLeverage',
          'VolatilityControlLeverage',
          'TermProtection',
          'ContinuousProtection'
        ],
        state.selectedStrategy.maxLeverageRatio,
        10
      ),
      message: 'Max leverage ratio cannot be greater than 10'
    })
  ],
  target_vols: [
    (state: EditStrategiesState) => ({
      isValid: isTripleValid(
        state.selectedStrategy.strategyTemplateId,
        ['RiskManaged', 'VolatilityControlLeverage', 'TermProtection', 'ContinuousProtection'],
        {
          min: state.selectedStrategy.minTargetVolatility,
          default: state.selectedStrategy.defaultTargetVolatility,
          max: state.selectedStrategy.maxTargetVolatility
        }
      ),
      message:
        'Valid target volatilities must be entered. Max must be greater than default, and default must be greater than min'
    })
  ],
  min_target_vol: [
    (state: EditStrategiesState) => ({
      isValid: validateMinValue(
        state.selectedStrategy.strategyTemplateId,
        ['RiskManaged', 'VolatilityControlLeverage', 'TermProtection', 'ContinuousProtection'],
        state.selectedStrategy.minTargetVolatility,
        0.01
      ),
      message: 'Min target volatility cannot be less than 1%'
    })
  ],
  max_target_vol: [
    (state: EditStrategiesState) => ({
      isValid: validateMaxValue(
        state.selectedStrategy.strategyTemplateId,
        ['RiskManaged', 'VolatilityControlLeverage', 'TermProtection', 'ContinuousProtection'],
        state.selectedStrategy.maxTargetVolatility,
        1
      ),
      message: 'Max target volatility cannot be greater than 100%'
    })
  ],
  maturity_months: [
    (state: EditStrategiesState) => ({
      isValid: isTripleValid(state.selectedStrategy.strategyTemplateId, ['TermProtection'], {
        min: state.selectedStrategy.minNumberOfMonthsToTimeToMaturity,
        default: state.selectedStrategy.minNumberOfMonthsToTimeToMaturity,
        max: state.selectedStrategy.maxNumberOfMonthsToTimeToMaturity
      }),
      message: 'Valid numbers of months must be entered. Max must be greater than min'
    })
  ],
  min_maturity_month: [
    (state: EditStrategiesState) => ({
      isValid: validateMinValue(
        state.selectedStrategy.strategyTemplateId,
        ['TermProtection'],
        state.selectedStrategy.minNumberOfMonthsToTimeToMaturity,
        1
      ),
      message: 'Min number of months to maturity must be at least 1'
    })
  ],
  max_maturity_month: [
    (state: EditStrategiesState) => ({
      isValid: validateMaxValue(
        state.selectedStrategy.strategyTemplateId,
        ['TermProtection'],
        state.selectedStrategy.maxNumberOfMonthsToTimeToMaturity,
        360
      ),
      message: 'Max target volatility cannot be greater than 360'
    })
  ],
  vol_index_trigger: [
    (state: EditStrategiesState) => ({
      isValid:
        validateMinValue(
          state.selectedStrategy.strategyTemplateId,
          [
            'ConstantProportionLeverage',
            'VolatilityControlLeverage',
            'TermProtection',
            'ContinuousProtection'
          ],
          state.selectedStrategy.volatilityIndexTriggerLevel,
          5
        ) &&
        validateMaxValue(
          state.selectedStrategy.strategyTemplateId,
          [
            'ConstantProportionLeverage',
            'VolatilityControlLeverage',
            'TermProtection',
            'ContinuousProtection'
          ],
          state.selectedStrategy.volatilityIndexTriggerLevel,
          100
        ),
      message: 'Volatility index trigger level must be greater than 5 and less than 100'
    })
  ],
  short_circuit: [
    (state: EditStrategiesState) => ({
      isValid:
        validateMinValue(
          state.selectedStrategy.strategyTemplateId,
          [
            'ConstantProportionLeverage',
            'VolatilityControlLeverage',
            'TermProtection',
            'ContinuousProtection'
          ],
          state.selectedStrategy.shortCircuitMaxAllocation,
          0.0001
        ) &&
        validateMaxValue(
          state.selectedStrategy.strategyTemplateId,
          [
            'ConstantProportionLeverage',
            'VolatilityControlLeverage',
            'TermProtection',
            'ContinuousProtection'
          ],
          state.selectedStrategy.shortCircuitMaxAllocation,
          state.selectedStrategy.maxLeverageRatio
        ),
      message:
        'Short circuit max allocation must be greater than 0.01% and less than the max leverage ratio of the strategy'
    })
  ],
  reduced_multiplier: [
    (state: EditStrategiesState) => ({
      isValid:
        validateMinValue(
          state.selectedStrategy.strategyTemplateId,
          ['TermProtection', 'ContinuousProtection'],
          state.selectedStrategy.shortCircuitReducedMultiplier,
          0.01
        ) &&
        validateMaxValue(
          state.selectedStrategy.strategyTemplateId,
          ['TermProtection', 'ContinuousProtection'],
          state.selectedStrategy.shortCircuitReducedMultiplier,
          10
        ),
      message: 'Short circuit reduced multiplier must be greater than 0.01 and less than 10'
    })
  ]
}
